import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import useLenis from "../hooks/useLenis";
import useSeo from "../hooks/useSeo";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { CapabilityDeck } from "../components/CapabilityDeck";
import { Reveal } from "../lib/reveal";
import { getCity } from "../data/cities";
import { getCaseStudy } from "../data/caseStudies";
import { getRegion } from "../data/regions";
import { MapPin, ArrowUpRight, Check } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

export default function City() {
  useLenis();
  const { slug } = useParams();
  const { lang, t } = useLang();
  const city = getCity(slug);

  useSeo(
    city
      ? {
          title: `OED in ${city.city}, ${city.state} — Landfill Odour Control | Fresh Earth`,
          description: city.intro,
          path: `/cities/${slug}`,
          jsonLd: {
            "@context": "https://schema.org",
            "@type": "Service",
            serviceType: "Landfill odour control & biological waste remediation",
            areaServed: { "@type": "City", name: city.city },
            provider: { "@type": "Organization", name: "Fresh Earth" },
            description: city.intro,
          },
        }
      : { title: "City not found · Fresh Earth", path: `/cities/${slug}` }
  );

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!city) {
    return (
      <div className="bg-bone text-ink min-h-screen flex flex-col items-center justify-center">
        <p className="font-serif text-3xl">City not found.</p>
        <Link to="/" className="mt-4 underline text-moss">Back home</Link>
      </div>
    );
  }

  const study = getCaseStudy(city.caseSlug);
  const region = getRegion(city.stateSlug);
  const H = lang === "hi" ? (city.hi || {}) : {};

  return (
    <div className="bg-bone text-ink min-h-screen" data-testid="city-page">
      <Navbar />
      <main className="pt-32 md:pt-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-slate">
            <Link to="/" className="hover:text-ink transition-colors">{t("Home", "होम")}</Link>
            <span className="text-stone">/</span>
            <Link to={`/regions/${city.stateSlug}`} className="hover:text-ink transition-colors">{region?.state}</Link>
            <span className="text-stone">/</span>
            <span className="text-ink">{city.city}</span>
          </div>

          <Reveal className="mt-8 flex items-center gap-3">
            <MapPin size={18} className="text-terracotta" />
            <span className="text-xs tracking-[0.22em] uppercase font-semibold text-moss">{city.city}, {city.state}</span>
          </Reveal>
          <Reveal>
            <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.95] mt-5 max-w-4xl">{t(city.headline, H.headline)}</h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-lg text-slate max-w-2xl">{t(city.intro, H.intro)}</p>
          </Reveal>

          {/* Stats */}
          <Reveal delay={0.15}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-14">
              {city.stats.map((s, i) => (
                <div key={s.l} className="bg-sand rounded-2xl border border-stone/20 p-8">
                  <div className="font-serif text-4xl md:text-5xl text-moss leading-none">{s.v}</div>
                  <div className="text-xs tracking-[0.1em] uppercase text-ink mt-4">{t(s.l, H.stats?.[i])}</div>
                </div>
              ))}
            </div>
          </Reveal>

          {/* Points + related case */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-16 items-start">
            <div className="lg:col-span-6">
              <Reveal>
                <h2 className="font-serif text-2xl md:text-3xl mb-6">{t("Why it matters here", "यह यहाँ क्यों मायने रखता है")}</h2>
              </Reveal>
              <ul className="space-y-4">
                {city.points.map((p, i) => (
                  <Reveal as="li" key={p} delay={i * 0.06} className="flex items-start gap-3">
                    <Check size={20} className="text-terracotta shrink-0 mt-0.5" />
                    <span className="text-base text-slate leading-relaxed">{t(p, H.points?.[i])}</span>
                  </Reveal>
                ))}
              </ul>
            </div>

            {study && (
              <Reveal className="lg:col-span-6">
                <Link to={`/case-studies/${study.slug}`} data-testid={`city-case-${study.slug}`} className="group block bg-sand rounded-3xl border border-stone/20 overflow-hidden hover:border-moss transition-colors duration-400">
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <img src={study.afterImg} alt={study.city} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <span className="absolute top-4 left-4 text-[0.68rem] tracking-[0.16em] uppercase bg-bone/90 text-forest px-3 py-1.5 rounded-full">{t("Case study", "केस स्टडी")}</span>
                  </div>
                  <div className="p-7">
                    <h3 className="font-serif text-2xl text-ink leading-tight">{t(study.headline, study.hi?.headline)}</h3>
                    <span className="inline-flex items-center gap-1 text-sm text-moss mt-3">{t("Read the full case study", "पूरी केस स्टडी पढ़ें")} <ArrowUpRight size={15} /></span>
                  </div>
                </Link>
              </Reveal>
            )}
          </div>

          {/* Gated capability deck */}
          <div className="mt-16">
            <CapabilityDeck context={`${city.city}, ${city.state}`} />
          </div>

          {/* CTA */}
          <Reveal>
            <div className="my-20 bg-forest text-bone rounded-3xl p-10 md:p-14 grain relative overflow-hidden">
              <p className="font-serif text-3xl md:text-4xl leading-snug relative max-w-2xl">{t(`Have a site in ${city.city}? Start with a low-risk pilot.`, `${city.city} में कोई साइट है? एक कम-जोखिम वाले पायलट से शुरुआत करें।`)}</p>
              <Link to="/#contact" className="inline-flex mt-8 items-center rounded-full bg-bone text-forest text-sm tracking-[0.1em] uppercase px-8 py-4 hover:bg-terracotta hover:text-bone transition-colors duration-300 relative">
                {t("Request a Pilot", "पायलट का अनुरोध करें")}
              </Link>
            </div>
          </Reveal>
        </div>
      </main>
      <Footer />
    </div>
  );
}
